/**
 * Next.js client-side instrumentation. Runs once at startup before any
 * user code. Wires up Sentry SDK -> GlitchReplay ingest, plus the
 * @glitchreplay/a11y axe-core scanner on dev / staging.
 *
 * Replaces the prior in-house components/GlobalErrorHandler.tsx, which
 * is left in place for the 14-day overlap window per the GlitchReplay
 * migration plan.
 */
import { axeIntegration } from "@glitchreplay/a11y";
import { networkProbeIntegration } from "@glitchreplay/network-probe";
import { cspIntegration } from "./lib/glitchreplay-csp";
import { initGlitchReplay, NOISE_DENY_URLS } from "./lib/glitchreplay-client";

// SDK init, noise filters and replay settings live in
// lib/glitchreplay-client.ts, shared with apps/tools and apps/blog. The CSP
// host filters below are main-app only.

// Adware / RUM-tracker domains injected by browser extensions into the page's
// main world (so they evade cspIntegration's chrome-extension:// scheme filter).
// Same class as WALLET_EXTENSION_CSP_HOSTS — keyed off the blocked host.
const TRACKER_ADWARE_CSP_HOSTS = [
  "rumt-zh.com",
  "trueshields.com",
  "h2tcbox.baidu.com",
  // Adscore ad-fraud/bot-detection beacon, injected by extensions and by some
  // ad partners' creatives. Suffix match covers the c.* / d.* subdomains.
  "adsco.re",
  // Browser-extension tracker already filtered from generic script-load
  // errors below (NOISE_MESSAGES-equivalent list); also trips a
  // script-src-elem CSP violation, which needs its own host filter here.
  "kp.apiget.ru",
];

// Browser extensions that inject their own stylesheets/fonts/scripts into
// every page the user visits. Their CSP violations name a real HTTPS host (not
// a chrome-extension:// URL), so cspIntegration's scheme filter misses them —
// same reason WALLET_EXTENSION_CSP_HOSTS exists. These are not reachable from
// our app and allow-listing them would only widen the policy for software we
// don't ship.
const BROWSER_EXTENSION_CSP_HOSTS = [
  // PixieBrix (browser automation extension) — font-src violations.
  "pixiebrix.com",
  // Slant.co comparison-widget extension — font-src violations.
  "slant.co",
  // Overbridge (page-monitoring extension) — connect-src beacons on every
  // page the visitor opens.
  "overbridgenet.com",
  // Wordtune writing assistant — pulls its design system into the host page.
  "wordtune.com",
  "pangram.com",
  // Deta Space personal-cloud apps. Extension backends are hosted at
  // <app>-<hash>.deta.app and injected as script-src-elem.
  "deta.app",
  // Huawei browser / HMS Core device telemetry (metrics-dr{a,e}.dt.…). The
  // "-dre" host was allow-listed once before it became clear this is the
  // browser's own analytics, not anything the page asks for; it rotates
  // subdomains, so filter the zone instead of chasing them into the policy.
  "dbankcloud.cn",
];

// Locally-installed software — extension companion apps, dev servers, media
// bridges — probed over http://127.0.0.1:<port> from a content script running
// in our page. Nothing we ship talks to loopback, and each new port mints a
// new issue (127.0.0.1:2040 through :2047 were eight separate issues), so
// match the whole loopback space at once. `host` still carries the port here.
const LOOPBACK_CSP_HOST_PATTERN =
  /^(?:127\.0\.0\.1|localhost|\[::1\])(?::\d+)?$/i;

// Enterprise secure-web-gateway proxies (Zscaler, Menlo, Soliton SecureWG)
// rewrite pages in-flight for corporate visitors and frame their own
// interstitials. These hosts are ALREADY in frame-src, so the violation is a
// phantom report from the proxy's own rewriting — nothing is actually blocked.
// Same class as FIRST_PARTY_BEACON_CSP_NOISE_HOSTS.
const ENTERPRISE_PROXY_CSP_NOISE_HOSTS = [
  "securewg.jp",
  "zscloud.net",
  "menlosecurity.com",
];

// Crypto-wallet browser extensions (Binance Wallet, etc.) inject an in-page
// provider into the document's main world, so the CSP violation they trip
// shows the page — not a chrome-extension:// URL — as its sourceFile and
// slips past cspIntegration's scheme filter. Key off the blocked host
// instead. Same class of noise as the "Failed to connect to MetaMask" entry
// in NOISE_MESSAGES — never reachable from our app.
const WALLET_EXTENSION_CSP_HOSTS = ["wallet.binance.com", "safesearchinc.com"];

// Social-share browser extensions ("Share to Facebook", FB Container, etc.)
// trip frame-src / form-action by opening Facebook share dialogs from any
// page the user visits. We don't render Facebook share buttons; adding
// www.facebook.com to frame-src/form-action would only widen the policy
// for extension-driven noise.
const SOCIAL_SHARE_NOISE_HOSTS = ["www.facebook.com"];

// Google Ads can trigger connect-src reports for DoubleClick endpoints even
// though the HTTPS origin is already in the enforced policy. Keep this out of
// GlitchReplay's error feed rather than broadening CSP for tracker noise.
const GOOGLE_ADS_CSP_NOISE_HOSTS = [
  "pagead2.googlesyndication.com",
  "static.doubleclick.net",
  "www.google.com",
];

// First-party RUM/analytics beacons whose HTTPS origin is ALREADY in the
// enforced connect-src — Cloudflare Web Analytics (static.cloudflareinsights.com
// + cloudflareinsights.com) and Microsoft Clarity (*.clarity.ms) — yet still
// emit a spurious connect-src violation report on some navigations. Same browser
// quirk that fires DoubleClick reports for an allowed origin
// (GOOGLE_ADS_CSP_NOISE_HOSTS above): nothing is actually blocked and no beacon
// is lost, so keep the phantom report out of GlitchReplay's feed rather than
// touching the (already-correct) policy. Suffix match covers the static.* /
// report.* subdomains the beacons rotate through.
const FIRST_PARTY_BEACON_CSP_NOISE_HOSTS = [
  "cloudflareinsights.com",
  "clarity.ms",
  "ssl-checker-func.azurewebsites.net",
  "rdap.org",
  "grow.me",
  "embed.contactcenterhq.com",
  // fonts.googleapis.com has been in connect-src since #43 (2026-06-11), but
  // GlitchReplay keeps reporting fresh "blocked" violations for it afterward
  // — same phantom-report quirk as the other entries here.
  "fonts.googleapis.com",
];

// Google Ads conversion pixels follow the user's locale — same campaign
// fires at www.google.com.gt, .co.uk, .ca, .co.in, .com.eg, .com.br, etc.
// The site only enumerates google.com / *.gstatic.com in img-src; the
// per-ccTLD variants are blocked but represent the same tracking pixel,
// not broken functionality. Allowing every Google ccTLD would weaken the
// policy for no rendering benefit. Filter on suffix instead.
const GOOGLE_CCTLD_PIXEL_PATTERN = /^www\.google\.[a-z.]+$/i;

initGlitchReplay({
  integrations: [
    axeIntegration({
      runOn: ["development", "staging", "production"],
      // RUM-style sampling: 1% of prod page loads run a single axe scan
      // via requestIdleCallback. Was 5% pre-#80 — dropped to 1% as a
      // safer trade-off against the INP regression that prompted #80,
      // while keeping prod coverage so new a11y regressions surface.
      // Fingerprint dedup means full rule coverage still accumulates
      // across visitors within a day at this rate.
      productionSampleRate: 0.01,
      wcagLevel: "AA",
      minImpact: "moderate",
    }),
    // Drops imagedelivery.net "Failed to load <img>" reports caused by
    // ad-blockers / privacy extensions / DNS filters at the network
    // layer. Verifies real failures via per-URL probe so genuine CDN
    // outages still surface (tagged gr.probe=reachable).
    // img.youtube.com: blog posts embed YouTube thumbnails as
    // maxresdefault.jpg with hqdefault.jpg fallback; videos that lack a
    // max-res thumb 404 by design (Counter-evidence: gr_8mz5sm9 for
    // 6xgeNhmm6Ag, HlfRTCcgPR8, uhiY3JSwFfw — all small channels).
    networkProbeIntegration({
      dropHosts: NOISE_DENY_URLS,
      verifyResourceErrors: [/imagedelivery\.net/i, /img\.youtube\.com/i],
    }),
    // Captures Content-Security-Policy violations via the browser's
    // `securitypolicyviolation` event. The CSP (next.config.mjs +
    // middleware.ts) carries no report-uri/report-to directive and Sentry
    // has no native CSP instrumentation, so without this a blocked
    // script/style/connect is invisible beyond the user's own console.
    // Extension-injected violations are filtered by URL scheme; wallet
    // extensions that inject into the page's main world need a host-based
    // filter (beforeCapture) on top. Local integration for now — see
    // lib/glitchreplay-csp.ts ("extract later").
    cspIntegration({
      beforeCapture: (violation) => {
        let host = "";
        try {
          host = new URL(violation.blockedURI).host;
        } catch {
          // blockedURI is not a full URL (bare hostname, keyword like "inline",
          // etc.) — use the raw value so host-based noise filters still apply.
          host = violation.blockedURI;
        }
        if (
          WALLET_EXTENSION_CSP_HOSTS.some(
            (h) => host === h || host.endsWith("." + h),
          )
        ) {
          return false;
        }
        if (SOCIAL_SHARE_NOISE_HOSTS.includes(host)) return false;
        if (
          TRACKER_ADWARE_CSP_HOSTS.some(
            (h) => host === h || host.endsWith("." + h),
          )
        ) {
          return false;
        }
        if (
          BROWSER_EXTENSION_CSP_HOSTS.some(
            (h) => host === h || host.endsWith("." + h),
          )
        ) {
          return false;
        }
        if (
          ENTERPRISE_PROXY_CSP_NOISE_HOSTS.some(
            (h) => host === h || host.endsWith("." + h),
          )
        ) {
          return false;
        }
        if (GOOGLE_ADS_CSP_NOISE_HOSTS.includes(host)) return false;
        if (
          FIRST_PARTY_BEACON_CSP_NOISE_HOSTS.some(
            (h) => host === h || host.endsWith("." + h),
          )
        ) {
          return false;
        }
        if (
          host !== "www.google.com" &&
          GOOGLE_CCTLD_PIXEL_PATTERN.test(host)
        ) {
          return false;
        }
        if (LOOPBACK_CSP_HOST_PATTERN.test(host)) return false;
        return true;
      },
    }),
  ],
});
